import React, { useState, useEffect } from 'react';
import useAnimatedBackground from '../hooks/useAnimatedBackground';
import PullDownMenu from './PullDownMenu';
import { useUI } from '../contexts/UIContext';

/**
 * Animasyonlu arka plan bileşeni
 * Fare hareketi ile etkileşimli halkalar gösterir ve animasyonlu gradyan içerir
 * 
 * @param {Object} props - Bileşen özellikleri
 * @param {boolean} props.isEnabled - Animasyonun etkin olup olmadığı
 * @param {boolean} props.disablePullDown - Aşağı çekme menüsünü devre dışı bırakmak için
 * @param {ReactNode} props.children - Alt bileşenler
 */
const AnimatedBackground = ({ isEnabled = true, disablePullDown = false, children }) => {
  const { ringStyle } = useAnimatedBackground(isEnabled);
  const { navigateToScreen, handleTouchStart, handleTouchMove, handleTouchEnd, pullDownProgress, showPullDownMenu, closePullDownMenu, showSecurityVerification, mousePosition } = useUI();
  const [touchStartY, setTouchStartY] = useState(0);
  const [gradientPos, setGradientPos] = useState({ x: 50, y: 30 });

  // Güvenlik doğrulama işleyicisi
  const handleVaultClick = () => {
    closePullDownMenu();
    
    if (showSecurityVerification) {
      showSecurityVerification(() => {
        navigateToScreen('digitalVault');
      });
    } else {
      navigateToScreen('digitalVault');
    }
  };

  // Dokunmatik olayların düzgün çalışması için optimize edilmiş işleyiciler
  const handleTouchStartOptimized = (e) => {
    if (disablePullDown) return;
    handleTouchStart(e);
  };
  
  const handleTouchMoveOptimized = (e) => {
    if (disablePullDown) return;
    handleTouchMove(e);
  };
  
  const handleTouchEndOptimized = (e) => {
    if (disablePullDown) return;
    handleTouchEnd(e);
  };

  // Gradient animasyonu
  useEffect(() => {
    if (!isEnabled) return;

    const interval = setInterval(() => {
      setGradientPos(prev => ({
        x: prev.x + (Math.random() * 2 - 1) * 3,
        y: prev.y + (Math.random() * 2 - 1) * 3
      }));
    }, 3000);

    return () => clearInterval(interval);
  }, [isEnabled]);

  // Fare/dokunma hareketi efekti
  useEffect(() => {
    if (!isEnabled || !mousePosition) return;

    // Mouse hareketi olduğunda gradyan pozisyonunu hafifçe hareket ettir
    if (mousePosition.x || mousePosition.y) {
      setGradientPos(prev => ({
        x: 50 + mousePosition.x / 20,
        y: 30 + mousePosition.y / 20
      }));
    }
  }, [isEnabled, mousePosition]);

  return (
    <div 
      className="animated-background min-h-screen relative overflow-hidden"
      onTouchStart={disablePullDown ? null : handleTouchStartOptimized}
      onTouchMove={disablePullDown ? null : handleTouchMoveOptimized}
      onTouchEnd={disablePullDown ? null : handleTouchEndOptimized}
      onMouseDown={disablePullDown ? null : handleTouchStartOptimized}
      onMouseMove={disablePullDown ? null : handleTouchMoveOptimized}
      onMouseUp={disablePullDown ? null : handleTouchEndOptimized}
      style={{
        background: `radial-gradient(circle at ${gradientPos.x}% ${gradientPos.y}%, rgba(59, 130, 246, 0.15) 0%, rgba(255, 255, 255, 0) 70%),
                    linear-gradient(120deg, rgba(96, 165, 250, 0.4) 0%, rgba(59, 130, 246, 0.1) 100%)`,
        transition: 'background 3s ease'
      }}
    >
      {/* Pull-down Menu */}
      {!disablePullDown && showPullDownMenu && (
        <PullDownMenu 
          onItemSelect={navigateToScreen}
          onShowSecurityVerification={showSecurityVerification}
        />
      )}

      {/* Animasyonlu Arka Plan */}
      <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
        {/* Üst büyük mavi halka */}
        <div 
          className="absolute w-[150%] h-[150%] rounded-full bg-gradient-to-r from-blue-600 to-cyan-500"
          style={{
            ...ringStyle,
            top: '-80%',
            left: '-25%',
            filter: 'blur(60px)',
            opacity: 0.6
          }}
        />
        
        {/* Alt mavi halka */}
        <div 
          className="absolute w-[100%] h-[100%] rounded-full bg-gradient-to-l from-blue-900 to-blue-600"
          style={{
            ...ringStyle,
            bottom: '-60%',
            left: '-30%',
            transitionDelay: '0.1s',
            filter: 'blur(60px)',
            opacity: 0.6
          }}
        />

        {/* Ek parlama efekti */}
        <div 
          className="absolute w-[70%] h-[70%] rounded-full bg-gradient-to-tr from-cyan-400 to-blue-300"
          style={{
            top: `${gradientPos.y - 50}%`,
            right: `${gradientPos.x - 60}%`,
            filter: 'blur(70px)',
            opacity: 0.3,
            transition: 'all 2s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        />
      </div>
      
      {/* İçerik */}
      <div className="z-10 flex flex-col items-center justify-center w-full max-w-md px-6">
        {children}
      </div>
    </div>
  );
};

export default AnimatedBackground;